"use client"

import { useQuery } from "@tanstack/react-query"
import Link from "next/link"
import type { AccountAddressFormData } from "@/api/account-addresses"
import { getAccountAddress } from "@/api/account-addresses"
import { AddressPreview } from "~/components/account/addresses/preview"
import { AddressLoadingSkeleton } from "~/components/account/addresses/preview/skeleton"

function hasAddressData(address?: AccountAddressFormData): boolean {
  if (!address) return false
  return !!(
    address.street ||
    address.zipCode ||
    address.city ||
    address.firstName ||
    address.phone ||
    address.cpf
  )
}

export default function MinhaContaEnderecosPage() {
  const billingQuery = useQuery({
    queryKey: ["account", "address", "billing"],
    queryFn: () => getAccountAddress("billing"),
    retry: false,
  })

  const shippingQuery = useQuery({
    queryKey: ["account", "address", "shipping"],
    queryFn: () => getAccountAddress("shipping"),
    retry: false,
  })

  const hasBillingData = hasAddressData(billingQuery.data?.address)
  const hasShippingData = hasAddressData(shippingQuery.data?.address)

  return (
    <section className='rounded-2xl border bg-white p-6 shadow-xs md:p-8'>
      <div className='space-y-8'>
        <p className='text-base text-muted-foreground leading-7'>
          Os enderecos a seguir serao usados na pagina de finalizacao de compra
          por padrao.
        </p>

        <div className='grid gap-8 md:grid-cols-2 md:gap-7'>
          <article className='space-y-4'>
            <h2 className='border-border border-b pb-3 font-semibold text-3xl text-foreground'>
              Endereco de cobranca
            </h2>

            <div className='space-y-2'>
              {billingQuery.isPending && <AddressLoadingSkeleton />}

              {billingQuery.isError && (
                <p className='text-base leading-7'>
                  Nao foi possivel carregar este endereco.
                </p>
              )}

              {billingQuery.isSuccess && !hasBillingData && (
                <p className='text-base leading-7'>
                  Voce ainda nao definiu este tipo de endereco.
                </p>
              )}

              {billingQuery.isSuccess &&
                hasBillingData &&
                billingQuery.data.address && (
                  <AddressPreview address={billingQuery.data.address} />
                )}

              <Link
                className='font-medium text-lg text-primary underline transition-opacity hover:opacity-80'
                href='/minha-conta/enderecos/cobranca'
              >
                {hasBillingData ? "Editar endereco" : "Adicionar endereco"}
              </Link>
            </div>
          </article>

          <article className='space-y-4'>
            <h2 className='border-border border-b pb-3 font-semibold text-3xl text-foreground'>
              Endereco de entrega
            </h2>

            <div className='space-y-2'>
              {shippingQuery.isPending && <AddressLoadingSkeleton />}

              {shippingQuery.isError && (
                <p className='text-base leading-7'>
                  Nao foi possivel carregar este endereco.
                </p>
              )}

              {shippingQuery.isSuccess && !hasShippingData && (
                <p className='text-base leading-7'>
                  Voce ainda nao definiu este tipo de endereco.
                </p>
              )}

              {shippingQuery.isSuccess &&
                hasShippingData &&
                shippingQuery.data.address && (
                  <AddressPreview address={shippingQuery.data.address} />
                )}

              <Link
                className='font-medium text-lg text-primary underline transition-opacity hover:opacity-80'
                href='/minha-conta/enderecos/entrega'
              >
                {hasShippingData ? "Editar endereco" : "Adicionar endereco"}
              </Link>
            </div>
          </article>
        </div>
      </div>
    </section>
  )
}
